import clsx from 'clsx'
import { useState } from 'react'

import {
    Button,
    CONTENT_CONTAINER_STYLES,
    HEADING_4_STYLES,
    HELPER_TEXT_STYLES,
    LinkButton,
    PARAGRAPH_SMALL_STYLES,
} from 'src/libs/design-system'
import { Appear } from 'src/libs/react-utils'
import { ArrowDownOutlinedIcon } from 'src/libs/icons'

import { FeatureBundle, Plan } from './types'

interface FeaturesComparisonTableProps<PlanKey extends string> {
    plans: Plan<PlanKey>[]
    featureBundles: FeatureBundle<PlanKey>[]
}

export function FeaturesComparisonTable<PlanKey extends string>({
    plans,
    featureBundles,
}: FeaturesComparisonTableProps<PlanKey>) {
    const [selectedPlanKey, setSelectedPlanKey] = useState(
        () => plans.find((p) => p.isMostPopular)!.key
    )

    return (
        <Appear>
            <article className={clsx(CONTENT_CONTAINER_STYLES, 'grid gap-12')}>
                <h2 className={HEADING_4_STYLES}>Features you will love</h2>

                <div className={clsx(PARAGRAPH_SMALL_STYLES, 'grid gap-8')}>
                    {featureBundles.map(({ title, features }, featureBundleIndex) => (
                        <table key={featureBundleIndex} className="text-center">
                            <thead>
                                <tr className="border-b border-grey-700/10">
                                    <th className="p-4 pt-6 pl-0 text-left align-top">{title}</th>
                                    {plans.map((plan) => {
                                        const isFirstFeatureBundle = featureBundleIndex === 0

                                        return (
                                            <th
                                                key={plan.key}
                                                className={clsx(
                                                    ' relative w-32 sm:w-40 align-top p-4 pt-6',
                                                    plan.isMostPopular && [
                                                        'text-blue-500',
                                                        isFirstFeatureBundle &&
                                                            'rounded-t-2xl bg-yellow-50',
                                                    ],
                                                    plan.key !== selectedPlanKey &&
                                                        'hidden lg:table-cell'
                                                )}
                                            >
                                                <div
                                                    className={
                                                        isFirstFeatureBundle
                                                            ? undefined
                                                            : 'lg:sr-only'
                                                    }
                                                >
                                                    <strong className="font-bold flex items-center justify-center">
                                                        {plan.title}
                                                        <ArrowDownOutlinedIcon className="w-4 h-4 ml-1 lg:hidden" />
                                                    </strong>
                                                    <div
                                                        className={clsx(
                                                            HELPER_TEXT_STYLES,
                                                            'text-grey-700'
                                                        )}
                                                    >
                                                        {plan.priceShort || plan.price}
                                                    </div>
                                                </div>
                                                <select
                                                    title="Select plan"
                                                    aria-label="Select plan"
                                                    value={selectedPlanKey}
                                                    onChange={(event) =>
                                                        setSelectedPlanKey(
                                                            event.target.value as typeof plan.key
                                                        )
                                                    }
                                                    className="absolute inset-0 opacity-0 cursor-pointer lg:hidden"
                                                >
                                                    {plans.map((plan) => (
                                                        <option key={plan.key} value={plan.key}>
                                                            {plan.title}
                                                        </option>
                                                    ))}
                                                </select>
                                            </th>
                                        )
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {features.map((feature, featureIndex) => (
                                    <tr key={featureIndex} className="border-b border-grey-700/10">
                                        <th className="text-left font-normal align-top p-4 pl-0">
                                            {feature.title}
                                        </th>
                                        {plans.map((plan) => (
                                            <td
                                                key={plan.key}
                                                className={clsx(
                                                    'align-top p-4',
                                                    plan.isMostPopular &&
                                                        'text-blue-500 bg-yellow-50',
                                                    plan.key !== selectedPlanKey &&
                                                        'hidden lg:table-cell'
                                                )}
                                            >
                                                {feature.planValues[plan.key]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                {featureBundleIndex === featureBundles.length - 1 && (
                                    <tr>
                                        <th />
                                        {plans.map((plan) => {
                                            const sharedCtaProps = {
                                                variant: 'primary',
                                                size: 'small',
                                                onClick: plan.ctaButton.onClick,
                                                className: plan.isMostPopular
                                                    ? undefined
                                                    : /*tw:*/ 'bg-grey-700 hover:bg-grey-700/90 hover:animate-none focus-visible:animate-none',
                                                children:
                                                    plan.ctaButton.labelShort ||
                                                    plan.ctaButton.label,
                                            } as const

                                            return (
                                                <td
                                                    key={plan.key}
                                                    className={clsx(
                                                        'p-4',
                                                        plan.isMostPopular &&
                                                            'bg-yellow-50 rounded-b-2xl',
                                                        plan.key !== selectedPlanKey &&
                                                            'hidden lg:table-cell'
                                                    )}
                                                >
                                                    {plan.ctaButton.href ? (
                                                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                                                        <LinkButton
                                                            href={plan.ctaButton.href}
                                                            {...sharedCtaProps}
                                                        />
                                                    ) : (
                                                        <Button {...sharedCtaProps} />
                                                    )}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    ))}
                </div>
            </article>
        </Appear>
    )
}
